import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh'
import { TransformNode } from '@babylonjs/core/Meshes/transformNode'
import { Tools } from '@babylonjs/core/Misc/tools'
import { ComponentType } from '@lindberg/configuration'
import AnimationTimer from '../AnimationTimer'
import { replaceFinish } from '../ConfigurationInterpreter'
import { Engraving, loadEngravingFont, loadSVG } from '../Engraving'
import { TrackException } from '../Telemetry'
import Part from './Part'

export default class Temple extends Part {
	engravings: Engraving[] = []

	constructor(parent: TransformNode, mirror: boolean = false) {
		super('TEMPLE', parent, mirror)
		this.mirror = mirror
	}

	override get isPrecious(): boolean {
		return (['TEMPLE'] as ComponentType[]).some(x => this.config.components[x].color?.startsWith('AU750') || this.config.components[x].color?.startsWith('PT950'))
	}

	get isKidTeen(): boolean {
		return this.config.productline === 'RIMKID' || this.config.productline === '1000TEEN'
	}

	override get filename(): string {
		const modelType = this.isPrecious ? 'Precious' : this.isKidTeen ? 'Kids' : 'Titanium'
		return this.config.components.TEMPLE ? `Temples/${modelType}/${[this.config.components.TEMPLE.design, this.config.components.TEMPLE.size].filter(x => x).join(' ')}` : null
	}

	override getMeshMaterialName(mesh: AbstractMesh): string {
		const detail = /^(((TEMPLE|WIRE|PLATE)-DETAIL)|TUBE|WIRE-INNER)(-[A-Z0-9]{2,})?$/
		const base = /^(TEMPLE|WIRE|PLATE|TITANIUM)(-[A-Z0-9]{2,})?$/
		const gemstone = /^GEMSTONE|DIAMOND(-[0-9]{1,})?$/

		if (detail.test(mesh.name)) {
			const finish = mesh.name.match(detail)[4]?.substring(1)
			return finish ? replaceFinish(this.config.components.TEMPLE_DETAIL?.color || this.config.components.TEMPLE.color, finish) : this.config.components.TEMPLE_DETAIL?.color || this.config.components.TEMPLE.color
		} else if (mesh.name == 'TEMPLE-HORN') {
			return this.config.components.TEMPLE_HORN.color
		} else if (base.test(mesh.name)) {
			const finish = mesh.name.match(base)[2]?.substring(1)
			return finish ? replaceFinish(this.config.components.TEMPLE.color, finish) : this.config.components.TEMPLE.color
		} else if (/^COVER|ACETATE|TEMPLE-ACETATE$/.test(mesh.name)) {
			return this.config.components.COVER?.color || this.config.components.TEMPLE_TIP?.color || this.config.components.TEMPLE_ACETATE.color
		} else if (mesh.name == 'TEMPLE-HORN') {
			return this.config.components.TEMPLE_HORN.color
		} else if (mesh.name == 'SNAP-ON') {
			return this.config.components.SNAP_ON?.color
		} else if (mesh.name == 'TIP') {
			return this.config.components.TEMPLE_TIP.color
		} else if (gemstone.test(mesh.name)) {
			const gemstoneIndex = parseInt(mesh.name.match(gemstone)[1]?.substring(1) || '0')
			return gemstoneIndex ? this.config.components[`GEMSTONE-${gemstoneIndex}` as ComponentType]?.color || this.config.components.GEMSTONE?.color : this.config.components.GEMSTONE?.color || 'DIAMOND/W'
		} else {
			console.error(mesh.name)
			throw (`Unknown element: (${mesh.name})`)
		}
	}

	override async preloadMaterials() {
		super.preloadMaterials()
		if (!this.config.disableEngraving) {
			await loadEngravingFont()
			// await loadSVG('Textures/Engraving/LINDBERG.svg')

			if (this.config.components.UPPERRIM?.color == 'HORN/H60') {
				await loadSVG('Textures/Engraving/GOAT.svg')
			}
		}
	}

	override swap(animated?: boolean): void {
		super.swap(animated)
		try {
			this.engravings.forEach(e => e.dispose())
			this.engravings = []
			if (!this.loadedFilename) return

			const side = this.mirror ? 'Left' : 'Right'

			if (this.mirror) {
				this.engravings.push(new Engraving(this, 'ENGRAVING-LINDBERG', this.config.components.TEMPLE?.color, this.mirror, 'inside-end', 'logo'))
			} else if (this.config.engraving) {
				this.engravings.push(new Engraving(this, 'ENGRAVING-USER', this.config.components.TEMPLE?.color, this.mirror, 'inside-end', 'text', this.config.engraving))
			}
			if (this.config.components.UPPERRIM?.color == 'HORN/H60') {
				this.engravings.push(new Engraving(this, 'ENGRAVING-GOAT-' + side, this.config.components.TEMPLE?.color, this.mirror, 'inside-start', 'image', 'Textures/Engraving/GOAT.svg'))
			}
		} catch (error) {
			TrackException(error as Error, { type: 'CreateEngraving' })
			console.warn('Unable to create engraving', error)
		}
	}

	close(animated: boolean = true) {
		if (!this.config.closingAngles) return
		const angle = Tools.ToRadians(this.config.closingAngles[this.mirror ? 0 : 1]) * (this.mirror ? -1 : 1)
		if (animated) {
			setTimeout(() => new AnimationTimer(this.getScene(), { target: this.rotation, property: 'y', from: this.rotation.y, to: angle }), this.mirror ? 0 : 250)
		} else {
			this.rotation.y = angle
		}
	}

	open(animated: boolean = true) {
		if (animated) {
			setTimeout(() => new AnimationTimer(this.getScene(), { target: this.rotation, property: 'y', from: this.rotation.y, to: 0 }), this.mirror ? 100 : 0)
		} else {
			this.rotation.y = 0
		}
	}
}
